import APITemplate from '@api/APITemplate'
import con from '@services/connection'
import { atendimentoConexao as endpoint } from '@modules/settings/constants/URL'
import { CONEXAO_REQUEST_TIMEOUT_MS } from '@modules/settings/constants/Atendimento'

// Nomes para os argumentos posicionais de `con.*` — ver `postComPrazo` abaixo.
const DATA_VAZIO = null
const TIPO_GET = 'get'
const NAO_IGNORAR_ERRO = false

// Só o fluxo de pareamento declara prazo: é o que depende da Evolution API e
// por isso o que estoura o gateway. As demais rotas seguem sem timeout.
const COM_PRAZO = { timeout: CONEXAO_REQUEST_TIMEOUT_MS }

/**
 * Chamadas do pareamento, com o prazo já aplicado.
 *
 * `con.post`/`con.get` têm assinatura POSICIONAL — `(url, data, [type,]
 * ignoreErro, options)` — e no call-site isso vira `null, 'get', false`, que não
 * diz nada a quem lê. Os dois wrappers nomeiam esses argumentos UMA vez, aqui,
 * e o resto do arquivo só passa a URL.
 *
 * @param {String} url
 * @returns {Promise<Object>} envelope AAWZ ({ status, message, value })
 */
const postComPrazo = (url) =>
  con.post(url, DATA_VAZIO, NAO_IGNORAR_ERRO, COM_PRAZO)

const getComPrazo = (url) =>
  con.get(url, DATA_VAZIO, TIPO_GET, NAO_IGNORAR_ERRO, COM_PRAZO)

class ApiAtendimentoConexao extends APITemplate {
  /**
   * List connections visible to the current user
   * Gestor sees all office connections; professional sees only linked
   */
  async listarConexoes() {
    const r = await con.get(endpoint.conexoes)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Create a new connection
   * @param {String} nome - Connection name
   * @param {String} tipo - 'compartilhado' or 'individual'
   * @param {Number} [id_aceite_termos] - Terms acceptance record ID
   * @param {Number} [id_profissional] - Optional. Gestor only: target
   *   professional to auto-link an individual connection to. Defaults to
   *   the logged-in user when omitted.
   */
  async criarConexao({ nome, tipo, id_aceite_termos, id_profissional } = {}) {
    if (!nome || !tipo) {
      return this.buildErrorResponse({
        status: 400,
        message: 'Nome e tipo da conexão são obrigatórios',
        value: null,
      })
    }
    const payload = { nome, tipo, id_aceite_termos }
    if (id_profissional) payload.id_profissional = id_profissional
    const data = { data: JSON.stringify(payload) }
    const r = await con.post(endpoint.conexoes, data)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  async getTermos() {
    const r = await con.get(endpoint.termos)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  async aceitarTermos() {
    const r = await con.post(endpoint.termosAceitar)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Start the QR code connection flow for a connection (POST to trigger backend)
   * @param {Number} id_conexao
   */
  async conectar({ id_conexao } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const r = await postComPrazo(endpoint.connect(id_conexao))
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Get QR code for a connection
   * @param {Number} id_conexao
   */
  async getQrCode({ id_conexao } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const r = await getComPrazo(endpoint.qrcode(id_conexao))
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Get single connection status
   * @param {Number} id_conexao
   */
  async getStatus({ id_conexao } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const r = await con.get(endpoint.statusSingle(id_conexao))
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Bulk status check for all visible connections
   */
  async getBulkStatus() {
    const r = await con.get(endpoint.status)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Disconnect a connection
   * @param {Number} id_conexao
   */
  async desconectar({ id_conexao } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const r = await con.post(endpoint.disconnect(id_conexao))
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Restart a connection to fix session issues (no QR code needed)
   * @param {Number} id_conexao
   */
  async reiniciar({ id_conexao } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const r = await con.post(endpoint.restart(id_conexao))
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Delete a connection.
   *
   * Normal flow: backend tries to remove the instance on Evolution and
   * locally. If Evolution is stuck, it answers `{ status: 409, value:
   * { can_force: true } }` WITHOUT deleting anything. Passing `force: true`
   * appends `?force=true` so the backend removes it locally even with
   * Evolution failing (the instance is left orphaned on the server).
   * @param {Number} id_conexao
   * @param {Boolean} [force=false] - Force local removal ignoring Evolution
   */
  async deletar({ id_conexao, force = false } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const baseUrl = endpoint.deletar(id_conexao)
    const url = force ? `${baseUrl}?force=true` : baseUrl
    const r = await con.delet(url)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * Update professional assignments for a shared connection (gestor only)
   * @param {Number} id_conexao
   * @param {Array<Number>} ids_profissional
   */
  async atualizarProfissionais({ id_conexao, ids_profissional } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const data = {
      data: JSON.stringify({ ids_profissional: ids_profissional || [] }),
    }
    const r = await con.post(endpoint.profissionais(id_conexao), data)
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }

  /**
   * List professionals for a connection
   * @param {Number} id_conexao
   */
  async listarProfissionais({ id_conexao } = {}) {
    if (!id_conexao)
      return this.buildErrorResponse({
        status: 400,
        message: 'ID da conexão não informado',
        value: null,
      })
    const r = await con.get(endpoint.profissionais(id_conexao))
    return (
      r || this.buildErrorResponse({ message: 'Erro inesperado', value: null })
    )
  }
}

const apiAtendimentoConexao = new ApiAtendimentoConexao()

export { apiAtendimentoConexao }
export default apiAtendimentoConexao
